/* eslint-disable react/jsx-props-no-spreading */
import React, { ForwardedRef } from 'react';
import {
  Box,
  FormLabel,
  Input,
  InputProps,
  useNumberInput,
} from '@chakra-ui/react';
import { useField, useFormState } from 'react-final-form';
import styled from '@emotion/styled';
import { get } from 'lodash-es';
import { FormValues } from 'types';
import { RoundButton } from 'components';
import { AddIcon, MinusIcon } from 'static/icons';
import { Control, MetaError } from './Control';

const InputStyled = styled(Input)`
  height: 36px;
  font-size: 20px;
  text-align: center;
  border-color: ${({ theme }) => theme.colors.asphalt[3]};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;

  &::-webkit-input-placeholder {
    color: ${({ theme }) => theme.colors.asphalt[1]};
    font-size: 14px;
  }
`;

export const PlacesInput: React.FC<
  InputProps & {
    name: string;
    label?: string;
    available?: number;
    mask?: Array<RegExp | string>;
  }
> = React.forwardRef(
  (
    { className, name, label, available, ...rest },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const { input } = useField(name);
    const { touched, errors } = useFormState<FormValues>();
    const isTouched = get(touched, name);
    const error = get(errors, name);

    const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
      useNumberInput({
        name,
        defaultValue: 1,
        min: 1,
        max: available || 1,
        step: 1,
        pattern: '[0-9]*',
        onChange: (_, valueAsNumber: number) => input.onChange(valueAsNumber),
      });
    const inc = getIncrementButtonProps();
    const dec = getDecrementButtonProps();
    const inputProps = getInputProps();

    return (
      <Control name={name} className={className} width="auto">
        {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gridGap="10px"
          maxW={155}
        >
          <RoundButton {...dec}>
            <MinusIcon />
          </RoundButton>
          <InputStyled
            {...inputProps}
            {...rest}
            isInvalid={error && isTouched}
            ref={ref}
          />
          <RoundButton {...inc}>
            <AddIcon />
          </RoundButton>
        </Box>
        <MetaError name={name} />
      </Control>
    );
  },
);
